// Notifications.jsx — the messages pushed to the customer app.
//
// ── A BUG THIS FIXES ──
// The screen this replaces imported useEffect and never called it. There was
// no effect on mount, none on the page number and none on the search box, so
// the list was fetched only as a side effect of creating, updating or deleting
// something. Opening the page showed an empty table and "no notifications
// yet", and paging or searching did nothing at all — the first create made the
// existing rows appear, which looks like the create having gone wrong.

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchNotifications,
  createNotification,
  updateNotification,
  deleteNotification,
  toggleNotificationStatus,
} from "../../../slice/Notification/notificationSlice";
import {
  PageShell,
  DataTable,
  Button,
  Toggle,
  RowActions,
  Modal,
  Field,
  Pager,
  PlusIcon,
} from "../component/AdminUi";

const PER_PAGE = 10;

const onDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

export default function Notifications() {
  const dispatch = useDispatch();
  const { notifications, loading, error, pagination } = useSelector(
    (s) => s.notification
  );

  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState("");

  /* The effect the old screen was missing. */
  useEffect(() => {
    dispatch(fetchNotifications({ page, limit: PER_PAGE, search: query }));
  }, [dispatch, page, query]);

  const openNew = () => {
    setEditing(null);
    setTitle("");
    setBody("");
    setProblem("");
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setTitle(row.title || "");
    setBody(row.description || "");
    setProblem("");
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setEditing(null);
    setTitle("");
    setBody("");
    setProblem("");
  };

  const save = async () => {
    if (!title.trim()) return setProblem("Give the notification a title.");
    if (!body.trim()) return setProblem("Write what it should say.");

    setSaving(true);
    setProblem("");

    const data = { title: title.trim(), description: body.trim() };

    try {
      if (editing) {
        await dispatch(updateNotification({ id: editing.id, data })).unwrap();
        dispatch(fetchNotifications({ page, limit: PER_PAGE, search: query }));
      } else {
        await dispatch(createNotification(data)).unwrap();
        setPage(1);
        dispatch(fetchNotifications({ page: 1, limit: PER_PAGE, search: query }));
      }
      close();
    } catch (err) {
      setProblem(err || "That did not save.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.title}"?`)) return;
    try {
      await dispatch(deleteNotification(row.id)).unwrap();
      dispatch(fetchNotifications({ page, limit: PER_PAGE, search: query }));
    } catch (err) {
      setProblem(err || "Could not delete it.");
    }
  };

  const rows = notifications || [];

  return (
    <PageShell
      title="Notifications"
      count={
        loading && !rows.length
          ? "Loading…"
          : pagination?.total != null
          ? `${pagination.total} written`
          : `${rows.length} shown`
      }
      search={{
        value: query,
        onChange: (v) => {
          setQuery(v);
          setPage(1);
        },
        placeholder: "Search notifications",
      }}
      actions={
        <Button kind="primary" onClick={openNew}>
          <PlusIcon />
          Write one
        </Button>
      }
    >
      <DataTable
        columns={["Title", "Message", "Sending", "Added", ""]}
        loading={loading}
        error={error}
        isEmpty={!loading && rows.length === 0}
        emptyTitle={query ? "Nothing matches that" : "No notifications yet"}
        emptyHint={
          query
            ? "Try a shorter search."
            : "These go to everyone with the app installed."
        }
      >
        {rows.map((row) => (
          <tr key={row.id}>
            <td>
              <span className="ui-who__name">{row.title}</span>
            </td>
            <td style={{ maxWidth: 420 }}>
              <span className="ui-muted">{row.description}</span>
            </td>
            <td>
              <Toggle
                on={row.status !== false}
                onChange={() => dispatch(toggleNotificationStatus(row.id))}
                label={
                  row.status !== false
                    ? "Being sent — tap to stop"
                    : "Stopped — tap to send"
                }
              />
            </td>
            <td className="ui-num">{onDate(row.createdAt)}</td>
            <td>
              <RowActions
                onEdit={() => openEdit(row)}
                onDelete={() => remove(row)}
              />
            </td>
          </tr>
        ))}
      </DataTable>

      <Pager
        page={page}
        totalPages={pagination?.totalPages}
        total={pagination?.total}
        perPage={PER_PAGE}
        onPage={setPage}
      />

      <Modal
        open={open}
        title={editing ? "Edit notification" : "Write a notification"}
        onClose={close}
        footer={
          <>
            <Button onClick={close}>Cancel</Button>
            <Button kind="primary" onClick={save} disabled={saving}>
              {saving ? "Saving…" : editing ? "Save changes" : "Add it"}
            </Button>
          </>
        }
      >
        {problem ? <div className="ui-alert ui-alert--error">{problem}</div> : null}

        <Field
          label="Title"
          required
          hint="What shows on the lock screen. Keep it short."
        >
          <input
            className="ui-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={65}
            placeholder="e.g. Fresh turmeric in today"
          />
        </Field>

        <Field label="Message" required>
          <textarea
            className="ui-input"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={4}
            maxLength={240}
            placeholder="The line underneath the title."
            style={{ resize: "vertical", minHeight: 96 }}
          />
        </Field>
      </Modal>
    </PageShell>
  );
}
